import '@backstage/plugin-catalog-react';
import {
  createBaseThemeOptions,
  createUnifiedTheme,
  themes as builtinThemes,
  genPageTheme,
  UnifiedThemeOptions,
} from '@backstage/theme';

import { PaletteOptions } from '@material-ui/core/styles/createPalette';

const lightBaseTheme = builtinThemes.light.getTheme('v5');
const darkBaseTheme = builtinThemes.dark.getTheme('v5');

// see https://backstage.io/docs/getting-started/app-custom-theme/
const tokenlyPalette: PaletteOptions = {
  primary: {
    main: '#0067d1', //--COLOR--action--text
  },
  secondary: {
    main: '#51a7ff', //--COLOR--action--secondary--text
  },
  error: {
    main: '#bd140a', //--COLOR--error--text
  },
  warning: {
    main: '#996500', // --COLOR--caution--text
  },
  info: {
    main: '#0067d1', // --COLOR--info--text
  },
  success: {
    main: '#25741e' // --COLOR--success--text
  },
  background: {
    default: '#ffffff', // --COLOR--surface--primary
    paper: '#ffffff'    // --COLOR--surface--primary
  },
  banner: {
    info: '#f0f7ff',  // --COLOR--info--surface
    error: '#fef1f1', // --COLOR--error--surface
    text: '#0067d1',  // --COLOR--info--text
    link: '#004e9e',  // --COLOR--info--text--hover
  },
  errorBackground: '#fef1f1',   // --COLOR--error--surface
  warningBackground: '#fff5e0', // --COLOR--caution--surface
  infoBackground: '#f0f7ff',    //--COLOR--info--surface
  navigation: {
    background: '#ffffff', // --COLOR--surface--primary
    color: '#3a424a',      // --COLOR--text--primary
    indicator: '#0067d1',  // --COLOR--action--text
    selectedColor: '#0067d1', // --COLOR--action--text
    navItem: {
      hoverBackground: '#f3f4f6', // --COLOR--surface--primary--hover
    },
    submenu: {
      background: '#ffffff', // --COLOR--surface--primary
    },
  },
  text: {
    primary: '#343c44', // --COLOR--text--primary
  },
  link: '#0067d1', // --COLOR--action--text,
};

const tokenlyPaletteDark: PaletteOptions = {
  ...tokenlyPalette,
  primary: {
    main: '#51a7ff', //--COLOR--action--text
  },
  secondary: {
    main: '#0580ff', //--COLOR--action--secondary--text
  },
  error: {
    main: '#f5483d', //--COLOR--error--text
  },
  warning: {
    main: '#ffbb38', // --COLOR--caution--text
  },
  info: {
    main: '#51a7ff', // --COLOR--info--text
  },
  success: {
    main: '#39b52f' // --COLOR--success--text
  },
  background: {
    default: '#24282d', // --COLOR--surface--secondary
    paper: '#24282d'    // --COLOR--surface--secondary
  },
  banner: {
    info: '#002142',  // --COLOR--info--surface
    error: '#3a0603', // --COLOR--error--surface
    text: '#51a7ff',  // --COLOR--info--text
    link: '#9eceff',  // --COLOR--info--text--hover
  },
  errorBackground: '#3a0603',   // --COLOR--error--surface
  warningBackground: '#332200', // --COLOR--caution--surface
  infoBackground: '#002142',    //--COLOR--info--surface
  navigation: {
    background: '#24282d', // --COLOR--surface--primary
    color: '#ffffff',      // --COLOR--text--primary
    indicator: '#51a7ff',  // --COLOR--action--text
    selectedColor: '#51a7ff', // --COLOR--action--text
    navItem: {
      hoverBackground: '#3a424a', // --COLOR--surface--secondary--hover
    },
    submenu: {
      background: '#24282d', // --COLOR--surface--secondary
    },
  },
  text: {
    primary: '#ffffff', // --COLOR--text--primary
  },
  link: '#51a7ff', // --COLOR--action--text,
};

const pageTheme = {
  home: genPageTheme({ colors: ['var(--COLOR--data-viz--eleven)'], shape: 'none' }),
  documentation: genPageTheme({colors: ['var(--COLOR--data-viz--eight)'], shape: 'none' }),
  tool: genPageTheme({ colors: ['var(--COLOR--data-viz--four)'], shape: 'none' }),
  service: genPageTheme({ colors: ['var(--COLOR--data-viz--one)'], shape: 'none' }),
  website: genPageTheme({ colors: ['var(--COLOR--data-viz--seven)'], shape: 'none' }),
  library: genPageTheme({ colors: ['var(--COLOR--data-viz--ten)'], shape: 'none' }),
  other: genPageTheme({ colors: ['var(--COLOR--data-viz--six)'], shape: 'none' }),
  app: genPageTheme({ colors: ['var(--COLOR--data-viz--twelve)'], shape: 'none' }),
  apis: genPageTheme({ colors: ['var(--COLOR--data-viz--five)'], shape: 'none' }),
};

const components: UnifiedThemeOptions['components'] = {
  MuiButton: {
  styleOverrides: {
    root: {
        fontWeight: '600',
        boxShadow: 'none',
    },
    containedPrimary: {
      boxShadow: 'none',
      borderColor: 'var(--COLOR--action--border)',
      backgroundColor: 'var(--COLOR--action--surface)',
      '&:hover': {
        borderColor: 'var(--COLOR--action--border--hover)',
        backgroundColor: 'var(--COLOR--action--surface--hover)',
      },
      '&:focus': {
        borderColor: 'var(--COLOR--action--focus)',
      },
    },
  },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '& + &': {
          borderTop: '1px solid var(--COLOR--border--primary)',
        },
        '&:hover': {
          backgroundColor: 'var(--COLOR--surface--primary--hover)',
        },
      },
    },
  },
  BackstageSidebar: {
    styleOverrides: {
      drawer: {
        borderRight: '1px solid var(--COLOR--border--primary)'
      },
    }
  },
  BackstageSidebarSubmenu: {
    styleOverrides: {
      title: {
        fontWeight: '800',
      },
    }
  },
  BackstageSidebarSubmenuItem: {
    styleOverrides: {
      label: {
        fontWeight: 'bold',
        overflow: 'visible',
      },
    }
  },
  BackstageSidebarItem: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: 'var(--COLOR--surface--primary--hover)',
        },
      },
       highlighted: {
        '& > .MuiBox-root:last-child': {
          borderRight: '1px solid var(--COLOR--border--primary)',
        },
       }
    },
  },
  BackstageHeader: {
    styleOverrides: {
      header: {
        backgroundImage: 'unset',
        boxShadow: 'unset',
        paddingBlock: '32px',
        borderBottom: '1px solid var(--COLOR--border--primary)'
      },
      title: {
        color: 'var(--COLOR--text--primary)',
        fontWeight: '800',
      },
      subtitle: {
        color: 'var(--COLOR--text--primary)',
        fontWeight: '600',
      },
      type: {
        color: 'var(--COLOR--text--primary)',
      }
    },
  },
  BackstageHeaderLabel: {
      styleOverrides: {
          label: {
               color: 'var(--COLOR--text--primary)',
          },
          value: {
               color: 'var(--COLOR--text--primary)',
               '& a': {
                 color: 'var(--COLOR--action--text)',
               },
               '& a:hover': {
                 color: 'var(--COLOR--action--text--hover)',
               }
          }
    }
  },
  BackstageContent: {
    styleOverrides: {
      root: {
        padding: 'var(--LAYOUT--size--lg)',
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      elevation: {
        boxShadow: 'none',
      },
      elevation1: {
        boxShadow: 'none',
      },
      elevation2: {
        boxShadow: 'none',
      },
      root: {
        boxShadow: 'none',
        border: '1px solid var(--COLOR--border--primary)',
      },
    },
  },
  // @ts-ignore
  PluginCatalogEntityContextMenu: {
    styleOverrides: {
      button: {
        color: 'var(--COLOR--text--primary)',
      },
    }
  },
  // @ts-ignore
  CatalogReactUserListPicker: {
    styleOverrides: {
      root: {
        backgroundColor: 'var(--COLOR--surface--secondary)',
      },
    },
  },
};

export const fastlyThemeLight = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...lightBaseTheme?.palette,
      ...tokenlyPalette,
    },
  }),
  pageTheme,
  fontFamily: 'Inter',
  // @ts-ignore Components doesn't necessarily include baseline overrides.
  components,
});

export const fastlyThemeDark = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...darkBaseTheme?.palette,
      ...tokenlyPaletteDark,
    },
  }),
  pageTheme,
  fontFamily: 'Inter',
  // @ts-ignore Components doesn't necessarily include baseline overrides.
  components,
});
